import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { getBackendUrl } from "../../config";
import { identityHeaders } from "../../utils/session";

// Practice as a short conversation: the tense's three shapes, then "your turn".
// Deliberately minimal — no avatars, no logo, no stacked cards. The panel is sized
// to sit beside the examples list without a lot of scrolling, and each shape is one
// line rather than a box.
//
// The examples come from the course's own questions: every stored question is one of
// its sentences with a single blank, so filling that blank with its own answer yields
// an example that is genuinely the course's. Anything that will not read cleanly once
// filled is dropped, and a missing shape is simply not shown — never invented.

const FORMS = ["affirmative", "interrogative", "negative"];

const LABEL = {
  affirmative: "Affirmative",
  interrogative: "Interrogative",
  negative: "Negative",
};

const LABEL_COLOUR = {
  affirmative: "text-emerald-600",
  interrogative: "text-sky-600",
  negative: "text-rose-600",
};

// What shape is this sentence? A shape check, nothing more — it looks at the question
// mark and the negator, and we say so rather than pretending to mark grammar.
const shapeOf = (text) => {
  const t = String(text || "").trim();
  if (/\?\s*$/.test(t)) return "interrogative";
  if (/\b(not|never|n't)\b/i.test(t)) return "negative";
  return "affirmative";
};

const exampleFrom = (question) => {
  const raw = String(question.question_title || "");
  const key = String(question.right_answer || "").trim().toLowerCase();
  const answer = question[`answer_title_${key}`];
  if (!answer) return null;
  // Only single-blank questions compose into an example. A two-blank stem ("What
  // __________ you __________?") has options that carry their own markers, so a
  // single fill would leave debris in the sentence.
  if ((raw.match(/_{2,}/g) || []).length !== 1) return null;
  if (/_/.test(String(answer))) return null;
  // Some stems run the blank straight into a word ("operates_____ all"), so the
  // filled result would read "operateson". Put the missing boundary back first.
  const spaced = raw.replace(/(\S)_{2,}/, "$1 _____").replace(/_{2,}(\S)/, "_____ $1");
  let filled = spaced.replace(/_{2,}/g, String(answer).trim());
  // many stems decorate the blank: the blank is the long run, but single underscores
  // sit either side of it ("We _ ______ _ a small company"). Clear that decoration so
  // the sentence reads as a sentence.
  filled = filled.replace(/\s*_+\s*/g, " ").replace(/\s+/g, " ").trim();
  // a filled blank can double up ("doesn't not like"); drop those rather than show a
  // student a sentence that is wrong
  if (/\bnot\b[^.]*\bnot\b/i.test(filled)) return null;
  if (/\b(\w+)\s+\1\b/i.test(filled)) return null;
  if (filled.length > 90) return null;
  return filled;
};

const pickExamples = (questions) => {
  const found = {};
  questions.forEach((q) => {
    const sentence = exampleFrom(q);
    if (!sentence) return;
    const shape = shapeOf(sentence);
    if (!found[shape]) found[shape] = sentence;
  });
  return found;
};

const Line = ({ who, children }) =>
  who === "teacher" ? (
    <motion.p
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="border-l-2 border-slate-200 pl-2 text-[13px] leading-snug text-slate-600"
    >
      {children}
    </motion.p>
  ) : (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="flex justify-end"
    >
      <span className="rounded-full bg-orange-500 px-3 py-1 text-[13px] leading-snug text-white">
        {children}
      </span>
    </motion.div>
  );

const SentencePracticeChat = ({ tenseId, tenseName, onSaved }) => {
  const [thread, setThread] = useState([]);
  const [shapes, setShapes] = useState({});
  const [expected, setExpected] = useState("affirmative");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [ready, setReady] = useState(false);
  const bottomRef = useRef(null);
  const askedRef = useRef({});
  const openedRef = useRef(false);

  const push = (message) => setThread((prev) => [...prev, message]);

  const askFor = (form) => {
    if (askedRef.current[form]) return;
    askedRef.current[form] = true;
    setExpected(form);
    push({ who: "teacher", text: `Your turn — write one ${form} sentence.` });
  };

  useEffect(() => {
    // the tense name arrives after the first render, so this effect runs twice; only
    // the first pass may open the conversation, or the shapes appear twice in the thread
    if (openedRef.current) return undefined;
    if (!tenseName) return undefined;
    openedRef.current = true;
    let cancelled = false;
    const open = async () => {
      let found = {};
      try {
        const url = await getBackendUrl();
        const res = await fetch(`${url}/quiz/${encodeURIComponent(tenseName || "")}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // a wide sample raises the odds that all three shapes are represented
          body: JSON.stringify({ nb_questions: 40 }),
        });
        if (res.ok) {
          const data = await res.json();
          found = pickExamples(data.questions || []);
        }
      } catch (err) {
        // no examples is a fine outcome — the lesson still works without them
      }
      if (cancelled) return;
      setShapes(found);
      push({ who: "teacher", shapes: FORMS.filter((f) => found[f]) });
      askFor("affirmative");
      setReady(true);
    };
    open();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenseName]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [thread]);

  const send = async (event) => {
    event.preventDefault();
    const sentence = draft.trim();
    if (!sentence || sending) return;
    setDraft("");
    setSending(true);
    push({ who: "student", text: sentence });

    // Saved exactly as before: same endpoint, same fields, so a sentence sent from
    // here still lands in the student's notepad.
    // One retry before telling a student their sentence was not saved: the failure
    // seen in the wild was transient (the endpoint answers 200 from the same page),
    // and a lost sentence is worse than a second request.
    let failure = "";
    let alreadyOnFile = false;
    const post = async () => {
      const url = await getBackendUrl();
      const body = new FormData();
      body.append("sentence", sentence);
      body.append("course_id", tenseId);
      body.append("sentence_type", shapeOf(sentence));
      const res = await fetch(`${url}/userupload`, {
        method: "POST",
        headers: identityHeaders(),
        body,
      });
      if (res.ok) return true;
      let detail = "";
      try {
        detail = (await res.text()).slice(0, 200);
      } catch (err) {
        detail = "";
      }
      // The sentence column is unique across the whole site, so a sentence the site
      // already holds comes back as a 400. The student asked for it to be kept and it
      // is kept — treating that as failure told them their sentence was lost.
      if (/unique key/i.test(detail)) {
        alreadyOnFile = true;
        return true;
      }
      failure = `HTTP ${res.status}${detail ? ` ${detail}` : ""}`;
      console.error("Sentence not saved:", failure, { url, course_id: tenseId });
      return false;
    };
    // One retry, for a genuinely transient failure. Not for a duplicate: that is a
    // settled answer, and asking again cannot change it.
    let saved = false;
    for (let attempt = 0; attempt < 2 && !saved && !alreadyOnFile; attempt += 1) {
      try {
        saved = await post();
      } catch (err) {
        failure = String(err && err.message ? err.message : err);
        console.error("Sentence not saved (threw):", failure, { course_id: tenseId });
        saved = false;
      }
      if (!saved && !alreadyOnFile) await new Promise((done) => setTimeout(done, 800));
    }

    if (!saved) {
      push({
        who: "teacher",
        text: `Could not save that one${failure ? ` (${failure})` : ""} — send it again.`,
      });
      setSending(false);
      return;
    }

    if (alreadyOnFile) {
      push({ who: "teacher", text: "That sentence is already saved on the site." });
    }

    const written = shapeOf(sentence);
    if (written === expected) {
      const next = FORMS[FORMS.indexOf(expected) + 1];
      if (next) {
        push({ who: "teacher", text: "Good." });
        askFor(next);
      } else {
        push({ who: "teacher", text: "That's all three. Saved to your notepad — now try the quiz." });
      }
    } else {
      // Say what the check looks at instead of pretending to mark grammar.
      if (expected === "interrogative") {
        push({
          who: "teacher",
          text: "That's a statement — no question mark. Start with Do / Does or a question word, and end with ?.",
        });
      } else if (expected === "negative") {
        push({ who: "teacher", text: "Nothing is negated there. Use not (or n't) this time." });
      } else {
        push({
          who: "teacher",
          text: `That reads as a ${written} sentence. For the affirmative, say it plainly: subject then verb.`,
        });
      }
      askFor(expected);
    }
    setSending(false);
    if (onSaved) onSaved();
  };

  return (
    <div className="flex flex-col">
      <div
        className="mb-2 flex max-h-[13rem] flex-col gap-1.5 overflow-y-auto pr-1"
        aria-live="polite"
      >
        <AnimatePresence initial={false}>
          {thread.map((message, index) =>
            message.shapes ? (
              <motion.ul
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="space-y-0.5 border-l-2 border-slate-200 pl-2"
              >
                <li className="text-[13px] leading-snug text-slate-500">
                  {tenseName ? `${tenseName} takes three shapes.` : "This tense takes three shapes."}
                </li>
                {message.shapes.map((form) => (
                  <li key={form} className="text-[13px] leading-snug">
                    <span className={`font-semibold ${LABEL_COLOUR[form]}`}>{LABEL[form]}</span>{" "}
                    <span className="italic text-slate-600">{shapes[form]}</span>
                  </li>
                ))}
              </motion.ul>
            ) : (
              <Line key={index} who={message.who}>
                {message.text}
              </Line>
            )
          )}
        </AnimatePresence>
        <div ref={bottomRef} />
      </div>

      <div className="mb-2 flex flex-wrap gap-1.5">
        {FORMS.map((form) => (
          <button
            key={form}
            type="button"
            onClick={() => askFor(form)}
            aria-pressed={expected === form && ready}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-200 ${
              expected === form && ready
                ? "border-slate-400 bg-slate-100 text-slate-700"
                : "border-slate-200 text-slate-400 hover:bg-slate-50"
            }`}
          >
            {LABEL[form]}
          </button>
        ))}
      </div>

      <form onSubmit={send}>
        <div className="flex items-end gap-1.5 rounded-full border border-slate-300 bg-white p-1 pl-3 focus-within:ring-2 focus-within:ring-orange-400">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(e);
              }
            }}
            rows="1"
            aria-label="Write your sentence"
            className="flex-1 resize-none border-0 bg-transparent py-1 text-[13px] text-gray-800 focus:outline-none"
            placeholder="Write your sentence — Enter to send"
          />
          <motion.button
            type="submit"
            whileTap={{ scale: 0.9 }}
            disabled={!draft.trim() || sending}
            title="Send your sentence"
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-orange-500 text-white transition-colors duration-200 hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <PaperAirplaneIcon className="h-4 w-4" />
          </motion.button>
        </div>
      </form>
    </div>
  );
};

export default SentencePracticeChat;
